// The "Issued bonuses" filters live in the URL (?tab=issued&status=pushed&page=2&from=…) so a
// filtered list can be linked. A player search goes in only as a user ID: emails stay out of
// the URL, as on the check tab.
import { ALL_MANAGERS, managerParam, STATUS_FILTERS } from './issuedList';

const FILTER_KEYS = ['status', 'page', 'from', 'to', 'manager', 'q'];
const DEFAULT_STATUS = 'pending';
// STATUS_ALL: the "All" filter in the URL (its filter key is empty).
const STATUS_ALL = 'all';
const STATUSES = STATUS_FILTERS.map((f) => f.key).filter(Boolean);
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const USER_ID = /^\d+$/;

// dateOr keeps a real calendar day: Date.parse rolls 2026-02-30 over to 2 March, so the day
// must survive the round trip.
const dateOr = (v) => {
  if (!v || !DATE.test(v)) return '';
  const t = Date.parse(v);
  return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === v ? v : '';
};
const userIdOr = (v) => (v && USER_ID.test(v) ? v : '');

const statusFrom = (raw) => {
  if (raw === STATUS_ALL) return '';
  return STATUSES.includes(raw) ? raw : DEFAULT_STATUS;
};

// filtersFromSearch reads the list filters from the URL query; unusable values fall back to
// the defaults (first page of pending comps, every manager, no dates). An inverted date range
// is swapped (ISO days compare as strings).
export const filtersFromSearch = (searchParams) => {
  const page = Number(searchParams.get('page'));
  const dates = [dateOr(searchParams.get('from')), dateOr(searchParams.get('to'))];
  const [from, to] = dates[0] && dates[1] && dates[0] > dates[1] ? [dates[1], dates[0]] : dates;
  return {
    status: statusFrom(searchParams.get('status')),
    page: Number.isInteger(page) && page > 0 ? page : 1,
    from,
    to,
    manager: searchParams.get('manager') || ALL_MANAGERS,
    q: userIdOr(searchParams.get('q')),
  };
};

// searchWithoutFilters returns a copy of the query without the list filters; other params stay.
export const searchWithoutFilters = (searchParams) => {
  const next = new URLSearchParams(searchParams);
  FILTER_KEYS.forEach((k) => next.delete(k));
  return next;
};

// searchWithFilters returns a copy of the query holding the filters: the status always (so the
// link says what it shows), the rest only when they narrow the list.
export const searchWithFilters = (searchParams, filters) => {
  const next = searchWithoutFilters(searchParams);
  next.set('status', filters.status || STATUS_ALL);
  if (filters.page > 1) next.set('page', String(filters.page));
  if (filters.from) next.set('from', filters.from);
  if (filters.to) next.set('to', filters.to);
  if (filters.manager !== ALL_MANAGERS) next.set('manager', filters.manager);
  if (userIdOr(filters.q)) next.set('q', filters.q);
  return next;
};

// searchWithPatch returns the query with some filters changed, back on the first page unless
// the patch sets one.
export const searchWithPatch = (searchParams, patch) => (
  searchWithFilters(searchParams, { ...filtersFromSearch(searchParams), page: 1, ...patch })
);

// issuedQuery: the API params for the filters.
export const issuedQuery = ({ status, page, from, to, manager, q }, pageSize) => ({
  page,
  page_size: pageSize,
  ...(status && { status }),
  ...(q && { q }),
  ...(from && { from }),
  ...(to && { to }),
  ...managerParam(manager),
});

// pageItems: the page buttons — the first, the last and the pages around the current one, with
// null for a gap. A gap of a single page shows that page instead.
export const pageItems = (page, lastPage) => {
  const shown = [...new Set([1, page - 1, page, page + 1, lastPage])]
    .filter((p) => p >= 1 && p <= lastPage)
    .sort((a, b) => a - b);
  return shown.flatMap((p, i) => {
    const gap = i === 0 ? 1 : p - shown[i - 1];
    if (gap === 1) return [p];
    if (gap === 2) return [p - 1, p];
    return [null, p];
  });
};

// LISTED_TABS: the page's tabs that list comps (?tab=issued / ?tab=stats), each with its filters.
export const LISTED_TABS = ['issued', 'stats'];

// tabSearch: the query for the next tab, without the checked player. Back on the tab a player was
// opened from (its tab param still set), every filter stays. The list and stats share the date
// and manager filters (the status, page and player search differ, so they go); the check tab
// drops them all.
export const tabSearch = (sp, next) => {
  if (LISTED_TABS.includes(next) && sp.get('tab') === next) {
    const url = new URLSearchParams(sp);
    url.delete('user_id');
    return url;
  }
  const url = searchWithoutFilters(sp);
  if (LISTED_TABS.includes(next)) {
    ['from', 'to', 'manager'].forEach((k) => { if (sp.has(k)) url.set(k, sp.get(k)); });
    url.set('tab', next);
  } else {
    url.delete('tab');
  }
  url.delete('user_id');
  return url;
};
